import { OperationVariables } from "@apollo/client/core/types";
import { AgGrid } from "@jsq/ds/core";
import {
  ObjectType,
  PayeeType,
  PaymentProvider,
  PaymentServicePaymentBatchStatus,
  PaymentStatus as PaymentStatusEnum,
} from "#graphql/supergraphEnums";
import {
  GetPaymentBatchesConnectionForExportQuery,
  GetPaymentBatchesConnectionQuery,
  PaginationArgs,
  PaymentBatchesFilterInput,
  PaymentStatus,
} from "#graphql/supergraphTypes";
import { formatCalendarDate } from "#utils/datetime_utils";
import { toDbId } from "#utils/graphql_utils";
import { MoneyType } from "../../../Money";
import { getCombinedStatusConfig } from "../../../shared/combinedStatusUtil";
import { PaymentExportRow, PaymentRowItem, PaymentType } from "../types";
import { batchStatusUIConfig } from "../util/batchStatusUtil";
import { paymentStatusUIConfig, PaymentStatusForUI } from "../util/paymentStatusUtils";
import {
  applyUserFilter,
  applyUserSort,
  PaymentBatchFilterModel,
  PaymentBatchSortModel,
} from "../util/tableFilterUtils";
import { preparePaginationQuery } from "../util/tablePaginationUtils";

/**
 * Provide the variables for the query
 * @param param0 sortModel and filterModel
 * @returns The required variables for the query
 */
const BASE_OBJECT_TYPES = [ObjectType.DistributionBatch, ObjectType.Payable];
const M3_OBJECT_TYPES = [ObjectType.CapitalCallBatch, ObjectType.TransactionBatch];

const toInputFilterVariables = (
  filterModel: unknown,
  sortModel: unknown,
  amountRef?: MoneyType,
  isGPX?: boolean,
  isM3Enabled?: boolean,
): PaymentBatchesFilterInput => {
  const objectTypes = isM3Enabled
    ? [...BASE_OBJECT_TYPES, ...M3_OBJECT_TYPES]
    : BASE_OBJECT_TYPES;
  const inputFilter: PaymentBatchesFilterInput = {
    objectTypes,
    paymentProviders: [PaymentProvider.Fides],
    // Exclude TreasuryDenied batches for GPx Treasury dashboard
    ...(isGPX
      ? { excludeBatchStatuses: [PaymentServicePaymentBatchStatus.TreasuryDenied] }
      : {}),
  };
  applyUserFilter(inputFilter, filterModel as PaymentBatchFilterModel, amountRef);
  applyUserSort(inputFilter, sortModel as PaymentBatchSortModel[]);
  return inputFilter;
};

/**
 * Provide the variables for the query
 * @param param0 sortModel and filterModel
 * @returns The required variables for the query
 */
const toPaginationVariables = (startRow: unknown): OperationVariables => {
  const paginationArgs: PaginationArgs = preparePaginationQuery(startRow as number);
  return paginationArgs;
};

/**
 * Perform the response manipulation and return the required parameters for the ag-grid
 * @param data received from the API response
 * @param isGPX whether this is GPX (true) or Canopy (false)
 * @returns manipulated date for the ag-grid
 */
const toLoadSuccessParams = (
  data: GetPaymentBatchesConnectionQuery,
  isGPX?: boolean,
): { resp: AgGrid.LoadSuccessParams; amountRef?: MoneyType } => {
  let rowData: PaymentRowItem[] = [];
  let amountRef: MoneyType | undefined;
  rowData =
    data.paymentBatchesConnection.edges?.map((batch) => {
      if (!amountRef) {
        amountRef = batch.node.netAmount;
      }
      return {
        id: batch.node.id,
        payee: getPayeeName(
          batch.node.referenceObject,
          batch.node.objectsCount,
          batch.node.payments,
        ),
        paymentDate:
          batch.node.payments.length > 0 && batch.node.payments[0].scheduledProcessDate
            ? formatCalendarDate(batch.node.payments[0].scheduledProcessDate) ?? ""
            : "",
        batchStatusConfig: batchStatusUIConfig(
          batch.node.status,
          batch.node.fundingPayment?.method,
        ),
        paymentStatus: paymentStatusUIConfig(batch.node.payments),
        combinedStatusConfig: getCombinedStatusConfig(
          batch.node.status,
          batch.node.payments,
          isGPX,
          batch.node.fundingPayment?.method,
        ),
        clientName: batch.node.arena.name,
        entityName: batch.node.entity.name,
        entityId: batch.node.entity.id,
        paymentAmount: batch.node.netAmount,
        paymentType: getPaymentType(batch.node.referenceObject),
        paymentSubtitleLabel: getPaymentSubtitleLabel(batch.node.referenceObject),
        batchId:
          batch.node.referenceObject && "id" in batch.node.referenceObject
            ? batch.node.referenceObject.id
            : undefined,
        payments: batch.node.payments,
        hostName: batch.node.arena.jsqHostname,
        paymentDescription: batch.node.paymentDescription,
        creatorID: batch.node.creatorId,
        status: batch.node.status,
        bankAccount: batch.node.bankAccount,
      };
    }) || [];
  return {
    resp: {
      rowData: rowData || [],
      rowCount: data.paymentBatchesConnection.totalCount,
    },
    amountRef: amountRef,
  };
};

/**
 * Map GraphQL PayeeType enum to display PaymentType
 */
const payeeTypeToPaymentType: Record<PayeeType, PaymentType> = {
  [PayeeType.Vendor]: "Vendor",
  [PayeeType.Entity]: "Entity",
  [PayeeType.Investment]: "Investment",
};

/**
 * Get the payment type based on the reference object type
 * - For DistributionBatchObjectType: returns paymentsLabel or "Distribution"
 * - For CapitalCallBatchObjectType: returns paymentsLabel
 * - For TransactionBatchObjectType: returns paymentsLabel (with subtitle if present)
 * - For PayableObjectType: returns the payee type (Vendor/Entity/Investment)
 */
export const getPaymentType = (object?: ReferenceObjectNode | null): PaymentType | null => {
  switch (object?.__typename) {
    case "DistributionBatchObjectType":
    case "CapitalCallBatchObjectType":
    case "TransactionBatchObjectType":
      return object.paymentsLabel ?? null;
    case "PayableObjectType":
      if (object.payee?.payeeType) {
        return payeeTypeToPaymentType[object.payee.payeeType];
      }
      return null;
    default:
      return null;
  }
};

export type ReferenceObjectNode = NonNullable<
  GetPaymentBatchesConnectionQuery["paymentBatchesConnection"]["edges"]
>[number]["node"]["referenceObject"];

/**
 * Get the subtitle label from the reference object (e.g. "Net" for Distribution (Net))
 */
export const getPaymentSubtitleLabel = (object?: ReferenceObjectNode | null): string | null => {
  switch (object?.__typename) {
    case "DistributionBatchObjectType":
    case "TransactionBatchObjectType":
      // Suppress subtitle for "Negative contribution"
      if (object.paymentsLabel?.toLowerCase() === "negative contribution") {
        return null;
      }
      return object.paymentsSubtitleLabel ?? null;
    default:
      return null;
  }
};

type PaymentWithPosition = {
  position?: { account?: { name?: string | null } | null } | null;
};

/**
 * Get the payee name from the reference object.
 * - For batch types (Distribution, CapitalCall, Transaction): returns the
 *   description when present. For single-position batches with no description,
 *   falls back to the receiving investor account name. Multi-position
 *   batches with no description return null (the column shows the entity name).
 * - For PayableObjectType: returns the payee name.
 */
export const getPayeeName = (
  referenceObject?: ReferenceObjectNode,
  objectsCount?: number,
  payments?: PaymentWithPosition[],
): string | null => {
  if (!referenceObject) return null;

  switch (referenceObject.__typename) {
    case "DistributionBatchObjectType":
    case "CapitalCallBatchObjectType":
    case "TransactionBatchObjectType":
      if (referenceObject.description) return referenceObject.description;
      if (objectsCount === 1) {
        return payments?.[0]?.position?.account?.name ?? null;
      }
      return null;
    case "PayableObjectType":
      return referenceObject.payee?.name ?? null;
    default:
      return null;
  }
};

/**
 * Map a per-payment status enum to its UI label used in the rendered table.
 */
const paymentStatusLabel: Record<PaymentStatus, string> = {
  [PaymentStatusEnum.Pending]: PaymentStatusForUI.NotTransmitted,
  [PaymentStatusEnum.Processing]: PaymentStatusForUI.Processing,
  [PaymentStatusEnum.Settled]: PaymentStatusForUI.Completed,
  [PaymentStatusEnum.Failed]: PaymentStatusForUI.Failed,
  [PaymentStatusEnum.Canceled]: PaymentStatusForUI.Canceled,
};

/**
 * Convert a Money minor-unit value to a plain decimal number suitable for CSV.
 * Returns a `number` (not a string) so spreadsheets render the cell as numeric
 * — preserving sum/sort/filter semantics — even for negative values that would
 * otherwise be neutralized by the CSV formula-injection escape.
 */
const formatMoneyForCsv = (money: { amount: number; precision: number }): number => {
  const divisor = Math.pow(10, money.precision);
  return money.amount / divisor;
};

/**
 * Flatten an export query response into one CSV row per individual payment,
 * repeating batch-level metadata on every row that belongs to the batch.
 */
const toExportRows = (data: GetPaymentBatchesConnectionForExportQuery): PaymentExportRow[] => {
  const edges = data.paymentBatchesConnection.edges ?? [];
  const rows: PaymentExportRow[] = [];
  edges.forEach((edge) => {
    const batch = edge.node;
    const batchStatusText = batchStatusUIConfig(
      batch.status,
      batch.fundingPayment?.method,
    ).label;
    const paymentType = getPaymentType(batch.referenceObject) ?? "";
    const payee = getPayeeName(batch.referenceObject, batch.objectsCount, batch.payments) ?? "";
    const description = batch.paymentDescription ?? "";
    batch.payments.forEach((payment) => {
      rows.push({
        clientName: batch.arena.name,
        entityName: batch.entity.name,
        paymentType,
        payee,
        batchStatus: batchStatusText,
        paymentStatus: paymentStatusLabel[payment.status] ?? payment.status,
        paymentAmount: formatMoneyForCsv(payment.amount),
        paymentCurrency: payment.amount.currency,
        paymentDescription: description,
        paymentDate: formatCalendarDate(payment.scheduledProcessDate) ?? "",
        paymentMethod: payment.method,
        investorAccountName: payment.position?.account?.name ?? "",
        positionId: payment.position?.id ? String(toDbId(payment.position.id)) : "",
        paymentId: payment.id,
      });
    });
  });
  return rows;
};

export const paymentBatchGQLFactory = {
  toInputFilterVariables,
  toPaginationVariables,
  toLoadSuccessParams,
  toExportRows,
};
